import { Box, Checkbox, FormControlLabel, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStoreByName } from '../../../hooks/use-stores.hooks';
import SiddurUnitsControl from './SiddurUnitsControl';

/**
 * The reading controls beside the text, where a desktop has the room.
 *
 * On a phone these live in the hamburger and the words get the whole
 * screen. On a wide screen the same controls sit in a column next to the
 * text: units per screen, and the continuous flow with its reads-per-verse.
 * Same store fields as the menu, so a change in one
 * place shows in the other.
 */
const SiddurSidePanel = () => {
  const { t } = useTranslation();
  const uiStore = useStoreByName('uiStore');

  return (
    <Box
      component="aside"
      sx={{
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        gap: 2.5,
        width: 220,
        flexShrink: 0,
        p: 2,
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: '14px',
        backgroundColor: '#fafafa',
        // The panel fills the row's height and scrolls inside it. Taller than
        // the row (its hints run long) it would spill out under the key and
        // the microphone button, as it did on a laptop at 1280px. That also
        // needed the old global `aside { height: 100vh }` rule gone: it set
        // the height outright, so neither stretch nor the scroll took effect.
        alignSelf: 'stretch',
        minHeight: 0,
        overflowY: 'auto',
      }}
    >
      <SiddurUnitsControl />

      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={uiStore.autoFlowEnabled}
              onChange={() => uiStore.setAutoFlowEnabled(!uiStore.autoFlowEnabled)}
            />
          }
          label={t('autoFlowSetting')}
        />
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
          {t('autoFlowSettingHint')}
        </Typography>
        {uiStore.autoFlowEnabled && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.75 }}>
              {t('autoFlowReadsPerVerse')}
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={uiStore.readsPerVerse}
              onChange={(_event, value) => {
                if (value) uiStore.setReadsPerVerse(Number(value));
              }}
            >
              {[1, 2, 3, 4].map((times) => (
                <ToggleButton key={times} value={times} sx={{ minWidth: 40 }}>
                  {times}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default observer(SiddurSidePanel);
